import { z } from 'zod';

import {
  stationOperationalSettingsStatusValues,
  type GetStationOperationalSettingsRequest,
  type GetStationOperationalSettingsResponse,
  type StationOperationalSettingsDto,
  type StationOperationalSettingsStatus,
  type UpdateStationOperationalSettingsRequest,
  type UpdateStationOperationalSettingsResponse,
} from './station-operational-settings.dto.js';

const timestampSchema = z.iso.datetime();
const statusReasonSchema = z.string().trim().min(1).max(500).nullable();
const changeReasonSchema = z.string().trim().min(1).max(500).optional();

function isValidIanaTimeZone(value: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: value }).format();
    return true;
  } catch {
    return false;
  }
}

const ianaTimeZoneSchema = z.string().trim().min(1).refine(isValidIanaTimeZone, {
  message: 'Invalid time zone',
});

export const StationOperationalSettingsStatusSchema: z.ZodType<StationOperationalSettingsStatus> = z.enum(
  stationOperationalSettingsStatusValues,
);

export const StationOperationalSettingsDtoSchema: z.ZodType<StationOperationalSettingsDto> = z.object({
  id: z.uuid(),
  companyId: z.uuid(),
  stationId: z.uuid(),
  operationalTimeZone: ianaTimeZoneSchema.nullable(),
  configVersion: z.number().int().positive(),
  status: StationOperationalSettingsStatusSchema,
  statusReason: statusReasonSchema,
  lastConfiguredAt: timestampSchema.nullable(),
  lastChangedByUserId: z.uuid().nullable(),
  lastChangeReason: statusReasonSchema,
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
});

export const GetStationOperationalSettingsRequestSchema: z.ZodType<GetStationOperationalSettingsRequest> = z.object({
  companyId: z.uuid(),
  stationId: z.uuid(),
});

export const GetStationOperationalSettingsResponseSchema: z.ZodType<GetStationOperationalSettingsResponse> = z.object({
  settings: StationOperationalSettingsDtoSchema,
});

export const UpdateStationOperationalSettingsRequestSchema: z.ZodType<UpdateStationOperationalSettingsRequest> =
  z.object({
    companyId: z.uuid(),
    stationId: z.uuid(),
    operationalTimeZone: ianaTimeZoneSchema,
    ifVersion: z.number().int().positive(),
    changeReason: changeReasonSchema,
  });

export const UpdateStationOperationalSettingsResponseSchema: z.ZodType<UpdateStationOperationalSettingsResponse> =
  z.object({
    settings: StationOperationalSettingsDtoSchema,
  });
